/**
 * Marketplace template detail page — public.
 * - Anonymous visitor: sees preview snippet + CTA "Buy for €X" → login required
 * - Authenticated buyer (legal_pro|business): sees preview + Buy button → Stripe checkout
 * - Author: sees preview only (own template — no buy button)
 * - Buyer who already paid: sees full anonymized_document + "Open in my library"
 */
import React, { useCallback, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Seo, productJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { toast } from "sonner";
import {
  ArrowLeft, Tag, ShoppingBag, Loader2, ShieldCheck, ExternalLink, BookOpen,
  Lock,
} from "lucide-react";

export default function MarketplaceTemplate() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { templateId, slug } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  useImperativeLoad(() => {
    // Two entry points: /marketplace/template/:templateId (legacy, still supported)
    // or the SEO slug URL /marketplace/t/:slug. Slug endpoint resolves → id.
    if (slug && !templateId) {
      api.get(`/seo/marketplace/${slug}`).then((r) => {
        api.get(`/marketplace/templates/${r.data.template_id}`).then((rr) => {
          setTpl(rr.data);
          setLoading(false);
        }).catch(() => setLoading(false));
      }).catch(() => setLoading(false));
      return;
    }
    api.get(`/marketplace/templates/${templateId}`).then((r) => {
      setTpl(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [templateId, slug]);

  const handleBuy = useCallback(async () => {
    const tid = tpl?.template_id || templateId;
    if (!tid) return;
    if (!user) {
      navigate("/auth?redirect=" + encodeURIComponent(`/marketplace/template/${tid}`));
      return;
    }
    setBuying(true);
    try {
      const r = await api.post(`/marketplace/templates/${tid}/checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data.already_owned) {
        toast.success(t("marketplace.detail_owns_it"));
        navigate("/marketplace/library");
        return;
      }
      if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      if (code === "MARKETPLACE_TIER_REQUIRED") {
        toast.error(t("marketplace.upgrade_cta_buy"));
        navigate("/pricing");
      } else {
        toast.error(t("marketplace.err_create"));
      }
    } finally {
      setBuying(false);
    }
  }, [user, tpl?.template_id, templateId, navigate, t]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0C1F] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#9ACD32]" />
      </div>
    );
  }

  if (!tpl) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0C1F] text-white">
        <p>404 — template not found</p>
      </div>
    );
  }

  const canBuyTier = user && (user.subscription_tier === "legal_pro" || user.subscription_tier === "business");

  const canonicalPath = tpl?.slug ? `/marketplace/t/${tpl.slug}` : `/marketplace/template/${tpl?.template_id || templateId}`;

  return (
    <div className="min-h-screen bg-[#0A0C1F] text-white" data-testid="marketplace-detail-page">
      <Seo
        title={`${tpl.title} — €${tpl.price_eur} — NEURACOM Marketplace`}
        description={tpl.description.slice(0, 160)}
        canonicalPath={canonicalPath}
        ogType="product"
        ogImage="/brand/og-marketplace.png"
        jsonLdList={[
          productJsonLd({
            name: tpl.title,
            description: tpl.description,
            price: tpl.price_eur,
            url: canonicalPath,
          }),
          breadcrumbJsonLd([
            { name: "Marketplace", path: "/marketplace" },
            { name: tpl.label, path: "/marketplace" },
            { name: tpl.title, path: canonicalPath },
          ]),
        ]}
      />
      <section className="mx-auto max-w-4xl px-6 py-10 lg:px-12">
        <button
          type="button"
          onClick={() => navigate("/marketplace")}
          className="mb-6 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
          data-testid="marketplace-detail-back"
        >
          <ArrowLeft className="h-3 w-3" /> {t("marketplace.detail_back")}
        </button>

        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]" data-testid="marketplace-detail-label">
              {tpl.label}
            </p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="marketplace-detail-title">
              {tpl.title}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              {t("marketplace.card_by")} <span className="font-medium text-slate-200">{tpl.author_display_name}</span>
              {" · "}
              {tpl.purchases_count} {t("marketplace.detail_purchases").toLowerCase()}
            </p>
          </div>
          <div className="rounded-2xl border border-[#9ACD32]/30 bg-[#9ACD32]/10 px-5 py-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#9ACD32]">EUR</p>
            <p className="font-mono text-3xl font-extrabold text-white" data-testid="marketplace-detail-price">
              €{tpl.price_eur}
            </p>
          </div>
        </div>

        <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#9ACD32]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#9ACD32]">
          <ShieldCheck className="h-3 w-3" /> {t("marketplace.detail_anon_badge")}
        </span>

        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300" data-testid="marketplace-detail-description">
          {tpl.description}
        </p>

        {tpl.tags?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {tpl.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2.5 py-1 text-[11px] text-slate-300">
                <Tag className="h-3 w-3" /> {tag}
              </span>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="mt-10">
          {tpl.owns_it && tpl.anonymized_document ? (
            <>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-[#9ACD32]">
                {tpl.is_author ? t("marketplace.detail_is_author") : t("marketplace.detail_owns_it")}
              </p>
              <Card className="border-slate-800 bg-white p-10 text-[#1a1a1a] shadow-2xl">
                <pre className="whitespace-pre-wrap break-words font-serif text-[14px] leading-[1.7] text-[#1a1a1a] [overflow-wrap:anywhere]" data-testid="marketplace-detail-full-doc">
                  {tpl.anonymized_document}
                </pre>
              </Card>
            </>
          ) : (
            <>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                {t("marketplace.detail_preview_label")}
              </p>
              <Card className="border-slate-800 bg-white/95 p-8 text-[#1a1a1a] shadow-xl">
                <pre className="whitespace-pre-wrap break-words font-serif text-[14px] leading-[1.7] text-[#1a1a1a] [overflow-wrap:anywhere]" data-testid="marketplace-detail-preview">
                  {tpl.preview_snippet}
                </pre>
                <div className="mt-6 flex items-center justify-center border-t border-slate-200 pt-6">
                  <p className="inline-flex items-center gap-2 text-xs text-slate-500">
                    <Lock className="h-3.5 w-3.5" /> {t("marketplace.detail_locked_hint")}
                  </p>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Sticky buy bar */}
        {!tpl.owns_it && (
          <div className="mt-8 flex flex-col items-stretch gap-3 rounded-2xl border border-slate-800 bg-slate-900/80 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-slate-400">{t("marketplace.detail_anon_badge")}</p>
              <p className="font-mono text-2xl font-extrabold text-white">€{tpl.price_eur}</p>
            </div>
            {canBuyTier ? (
              <Button
                onClick={handleBuy}
                disabled={buying}
                className="rounded-full bg-[#9ACD32] px-6 py-6 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="marketplace-buy-btn"
              >
                {buying ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    {t("marketplace.detail_buy_cta").replace("{price}", tpl.price_eur)}
                  </>
                )}
              </Button>
            ) : user ? (
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-6 py-3 text-sm font-bold text-[#F4C955] hover:bg-[#D4A937]/20"
                data-testid="marketplace-buy-locked"
              >
                {t("marketplace.detail_buy_locked_cta")}
              </Link>
            ) : (
              <Link
                to={`/auth?redirect=${encodeURIComponent(`/marketplace/template/${tpl?.template_id || templateId}`)}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9ACD32] px-6 py-3 text-sm font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="marketplace-buy-login"
              >
                {t("marketplace.detail_buy_cta").replace("{price}", tpl.price_eur)}
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        )}

        {tpl.owns_it && !tpl.is_author && (
          <div className="mt-8 text-center">
            <Link
              to="/marketplace/library"
              className="inline-flex items-center gap-2 rounded-full bg-[#D4A937] px-6 py-3 text-sm font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="marketplace-detail-library-cta"
            >
              <BookOpen className="h-4 w-4" /> {t("marketplace.success_cta_library")}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
